'use client';

import {
  ModButton,
  ModModal,
  ModModalContent
} from '@moderneinc/design-system-components';
import { spacing, typography } from '@moderneinc/design-system-tokens';
import { Stack, Typography } from '@mui/material';
import type { FunctionComponent } from 'react';
import { useTourEngineStore } from '../../stores/tour-engine.store';
import { TRIAL_TOUR_ID } from './trial-tour.config';

/**
 * Intro modal for the trial tour (status 'welcome'), shown after landing via
 * ?trial=1. Copy branches on whether this browser has done a trial before —
 * captured by `useTrialEntry` into `trialIsReturning` before it writes the
 * moderne_try_seen cookie, since reading the cookie here would always see it
 * as already set by the time this modal opens.
 */
export const TrialWelcomeModal: FunctionComponent = () => {
  const status = useTourEngineStore((state) => state.status);
  const activeTourId = useTourEngineStore((state) => state.activeTourId);
  const trialIsReturning = useTourEngineStore(
    (state) => state.trialIsReturning
  );
  const begin = useTourEngineStore((state) => state.begin);
  const dismiss = useTourEngineStore((state) => state.dismiss);
  const open = status === 'welcome' && activeTourId === TRIAL_TOUR_ID;
  const isReturning = open && Boolean(trialIsReturning);

  return (
    <ModModal open={open} onClose={dismiss} maxWidth="sm" fullWidth>
      <ModModalContent>
        <Stack
          sx={{
            alignItems: 'center',
            textAlign: 'center',
            gap: `${spacing.spacing_2}px`,
            px: { xs: 1, sm: `${spacing.spacing_4}px` },
            py: `${spacing.spacing_3}px`
          }}
        >
          <Typography
            sx={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize.h1,
              fontWeight: typography.fontWeight.bold,
              lineHeight: 1.2
            }}
          >
            {isReturning ? 'Welcome back' : "You're all set"}
          </Typography>
          <Typography variant="body">
            Let&apos;s run this recipe on a sample repository — no setup or
            repositories of your own required. It only takes a minute.
          </Typography>
          <ModButton
            variant="primary"
            size="medium"
            autoFocus
            onClick={begin}
            sx={{ mt: 1, alignSelf: 'stretch' }}
          >
            Show me
          </ModButton>
        </Stack>
      </ModModalContent>
    </ModModal>
  );
};
